// CMYK ↔ RGB — pure conversion utilities (R2.5 flyout redesign, 2026-07-20).
//
// NAIVE DEVICE-INDEPENDENT MATH by design: no ICC profile, no ink limits —
// cmykToRgb is the standard complement formula, so entering CMYK sets the
// NEAREST RGB the screen can show. That is the honest ceiling until R4's
// print-honest color work (gamut util + profiles) lands; R4 replaces the
// bodies here without touching the callers (the color flyout's numeric
// entry speaks only through these four functions).
//
// Shapes: rgb {r,g,b} 0–255 ints · cmyk {c,m,y,k} 0–100 ints · hex '#rrggbb'.

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, Number.isFinite(v) ? v : lo));

/** '#rgb' | '#rrggbb' → {r,g,b} (0–255). Bad input → black. */
export function hexToRgb(hex) {
  if (typeof hex !== 'string') return { r: 0, g: 0, b: 0 };
  let h = hex.trim().replace(/^#/, '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return { r: 0, g: 0, b: 0 };
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

/** {r,g,b} (0–255, clamped/rounded) → '#rrggbb'. */
export function rgbToHex({ r, g, b } = {}) {
  const to2 = (v) => Math.round(clamp(v, 0, 255)).toString(16).padStart(2, '0');
  return `#${to2(r)}${to2(g)}${to2(b)}`;
}

/** {r,g,b} 0–255 → {c,m,y,k} 0–100 (naive complement; pure black → k 100). */
export function rgbToCmyk({ r, g, b } = {}) {
  const R = clamp(r, 0, 255) / 255;
  const G = clamp(g, 0, 255) / 255;
  const B = clamp(b, 0, 255) / 255;
  const k = 1 - Math.max(R, G, B);
  if (k >= 1) return { c: 0, m: 0, y: 0, k: 100 };
  const d = 1 - k;
  return {
    c: Math.round(((1 - R - k) / d) * 100),
    m: Math.round(((1 - G - k) / d) * 100),
    y: Math.round(((1 - B - k) / d) * 100),
    k: Math.round(k * 100),
  };
}

/** {c,m,y,k} 0–100 → nearest {r,g,b} 0–255 (naive complement). */
export function cmykToRgb({ c, m, y, k } = {}) {
  const C = clamp(c, 0, 100) / 100;
  const M = clamp(m, 0, 100) / 100;
  const Y = clamp(y, 0, 100) / 100;
  const K = clamp(k, 0, 100) / 100;
  return {
    r: Math.round(255 * (1 - C) * (1 - K)),
    g: Math.round(255 * (1 - M) * (1 - K)),
    b: Math.round(255 * (1 - Y) * (1 - K)),
  };
}
